/**
 * Endpoints de la categoría *Configuración*: Logs del Sistema y Operadores.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  ContenidoLog,
  DetalleOperador,
  FiltrosLog,
  ListadoLogs,
  Operador,
  OperadorPayload,
  ResumenLimpiarLogs,
} from './configuracion.types';

export const configuracionKeys = {
  all: ['configuracion'] as const,
  logs: (origen: string) => ['configuracion', 'logs', origen] as const,
  log: (filtros: FiltrosLog) =>
    [
      'configuracion',
      'log',
      filtros.origen,
      filtros.archivo,
      filtros.nivel,
      filtros.buscar,
      filtros.logger,
      filtros.desde,
      filtros.hasta,
      filtros.lineas,
    ] as const,
  operadores: ['configuracion', 'operadores'] as const,
  operador: (id: number | null, periodo?: { fecha_inicio: string; fecha_fin: string }) =>
    ['configuracion', 'operador', id ?? 0, periodo?.fecha_inicio ?? '', periodo?.fecha_fin ?? ''] as const,
} as const;

export const configuracionApi = {
  logs: (origen: string, signal?: AbortSignal): Promise<ListadoLogs> =>
    apiClient.get<ListadoLogs>('/api/system_logs', { query: { origen }, signal }),

  contenido: (filtros: FiltrosLog, signal?: AbortSignal): Promise<ContenidoLog> =>
    apiClient.get<ContenidoLog>(`/api/system_logs/${encodeURIComponent(filtros.archivo ?? '')}`, {
      query: {
        origen: filtros.origen,
        lineas: filtros.lineas,
        nivel: filtros.nivel === 'todos' ? '' : filtros.nivel,
        buscar: filtros.buscar,
        logger: filtros.logger,
        desde: filtros.desde,
        hasta: filtros.hasta,
      },
      signal,
    }),

  vaciar: (origen: string, archivo: string): Promise<{ mensaje: string }> =>
    apiClient.post<{ mensaje: string }>(
      `/api/system_logs/${encodeURIComponent(archivo)}/vaciar`,
      { query: { origen } },
    ),

  eliminar: (origen: string, archivo: string): Promise<{ mensaje: string }> =>
    apiClient.delete<{ mensaje: string }>(`/api/system_logs/${encodeURIComponent(archivo)}`, {
      query: { origen },
    }),

  limpiarTodos: (origen: string): Promise<ResumenLimpiarLogs> =>
    apiClient.delete<ResumenLimpiarLogs>('/api/system_logs', { query: { todos: 1, origen } }),

  operadores: (signal?: AbortSignal): Promise<Operador[]> =>
    apiClient.get<Operador[]>('/api/operadores', { signal }),

  operador: (
    id: number,
    periodo?: { fecha_inicio: string; fecha_fin: string },
    signal?: AbortSignal,
  ): Promise<DetalleOperador> =>
    apiClient.get<DetalleOperador>(`/api/operadores/${id}`, {
      query: {
        fecha_inicio: periodo?.fecha_inicio,
        fecha_fin: periodo?.fecha_fin,
      },
      signal,
    }),

  crearOperador: (payload: OperadorPayload): Promise<{ operador: Operador; mensaje: string }> =>
    apiClient.post<{ operador: Operador; mensaje: string }>('/api/operadores', { body: payload }),

  actualizarOperador: (
    id: number,
    payload: OperadorPayload,
  ): Promise<{ operador: Operador; mensaje: string }> =>
    apiClient.put<{ operador: Operador; mensaje: string }>(`/api/operadores/${id}`, {
      body: payload,
    }),

  cambiarEstado: (id: number, activo: boolean): Promise<{ mensaje: string }> =>
    apiClient.patch<{ mensaje: string }>(`/api/operadores/${id}/estado`, { body: { activo } }),

  eliminarOperador: (id: number): Promise<{ mensaje: string }> =>
    apiClient.delete<{ mensaje: string }>(`/api/operadores/${id}`),
} as const;

/** Archivos de log de una fuente (se refrescan cuando el usuario lo pide). */
export function useArchivosLog(origen: string, enabled = true) {
  return useQuery({
    queryKey: configuracionKeys.logs(origen),
    queryFn: ({ signal }) => configuracionApi.logs(origen, signal),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Contenido del log seleccionado.
 *
 * `enVivo` refresca cada 5 s (el panel anterior no lo hacía; se deja como opción
 * para seguir un log mientras se reproduce un problema).
 */
export function useContenidoLog(filtros: FiltrosLog, enVivo = false, enabled = true) {
  return useQuery({
    queryKey: configuracionKeys.log(filtros),
    queryFn: ({ signal }) => configuracionApi.contenido(filtros, signal),
    enabled: enabled && Boolean(filtros.archivo),
    refetchInterval: enVivo ? 5_000 : false,
    staleTime: 0,
  });
}

/** Operadores de pago configurados. */
export function useOperadores(enabled = true) {
  return useQuery({
    queryKey: configuracionKeys.operadores,
    queryFn: ({ signal }) => configuracionApi.operadores(signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Detalle de un operador con sus ventas y comisiones del período. */
export function useOperador(
  id: number | null,
  periodo?: { fecha_inicio: string; fecha_fin: string },
  enabled = true,
) {
  return useQuery({
    queryKey: configuracionKeys.operador(id, periodo),
    queryFn: ({ signal }) => configuracionApi.operador(id as number, periodo, signal),
    enabled: enabled && Boolean(id),
    staleTime: 30_000,
  });
}

/** Mutaciones de logs: vaciar, eliminar y limpiar todo (siempre sobre una fuente). */
export function useMutarLogs() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: configuracionKeys.all });

  return {
    vaciar: useMutation({
      mutationFn: ({ origen, archivo }: { origen: string; archivo: string }) =>
        configuracionApi.vaciar(origen, archivo),
      onSuccess: refrescar,
    }),
    eliminar: useMutation({
      mutationFn: ({ origen, archivo }: { origen: string; archivo: string }) =>
        configuracionApi.eliminar(origen, archivo),
      onSuccess: refrescar,
    }),
    limpiarTodos: useMutation({
      mutationFn: (origen: string) => configuracionApi.limpiarTodos(origen),
      onSuccess: refrescar,
    }),
  };
}

/** Mutación de operadores (alta, edición, estado y baja). */
export function useMutarOperadores() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: configuracionKeys.operadores });

  return {
    crear: useMutation({ mutationFn: configuracionApi.crearOperador, onSuccess: refrescar }),
    actualizar: useMutation({
      mutationFn: ({ id, payload }: { id: number; payload: OperadorPayload }) =>
        configuracionApi.actualizarOperador(id, payload),
      onSuccess: refrescar,
    }),
    cambiarEstado: useMutation({
      mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
        configuracionApi.cambiarEstado(id, activo),
      onSuccess: refrescar,
    }),
    eliminar: useMutation({ mutationFn: configuracionApi.eliminarOperador, onSuccess: refrescar }),
  };
}
